import { Alignment, Borders, Cell, FillPattern, Font, Row, Workbook, Worksheet } from 'exceljs';
import { get, set, uniqBy } from 'lodash';
import { saveAs } from 'file-saver';
import moment from 'moment';
import {
  convertNumberToFourDigitsString,
  formatDateToDisplay,
  getContributionLevelTextFromDrops,
  sumByField,
  extractQuarterFromName
} from '@/helpers';
import {
  AssessmentPeriodDTO,
  UserContributionLevelDisplayDTO,
  UserContributionLevelPeriodDTO,
  ProjectInformationDTO
} from '@/models';
import { fetchPeriodsContributionLevels } from '@/services';
import AssessmentPeriodExport from '@/services/assessment-period-export';
import { STYLES, NA } from './project-exporting-config';

type Style = {
  fill?: FillPattern;
  border?: Partial<Borders>;
  font?: Partial<Font>;
  alignment?: Partial<Alignment>;
};

type callbackFn = (cell: Cell, index: number) => void;
const EMPTY = '';

export default class ExportByUser {
  private workbook = new Workbook();
  private ws: Worksheet;
  private assessmentPeriods: AssessmentPeriodDTO[] = [];
  private usersExport: UserContributionLevelDisplayDTO[] = [];
  private columnWidths = [8, ...Array(6).fill(30)];
  private periodsContributions: UserContributionLevelPeriodDTO[] = [];

  constructor(assessmentPeriods: AssessmentPeriodDTO[], users: UserContributionLevelDisplayDTO[]) {
    this.assessmentPeriods = assessmentPeriods;
    this.usersExport = users;
  }

  async generateXLSXFile(): Promise<void> {
    const { data: periodsContributionsData } = await fetchPeriodsContributionLevels();
    this.periodsContributions = periodsContributionsData;
    if (this.assessmentPeriods.length >= 2) {
      const sheetName = AssessmentPeriodExport.buildAssessmentSheetName('Summary Information');
      this.ws = this.workbook.addWorksheet(sheetName);
      const userContributionsOfPeriod = this.buildDisplayPeriodsContributionLevels('', this.usersExport, true);
      this.addUserInformationToWorksheet(userContributionsOfPeriod, true);
      this.setColumnWidth();
      this.assessmentPeriods.forEach(as => {
        this.generatePeriod(as);
      });
    } else this.generatePeriod(this.assessmentPeriods[0]);
    try {
      const buf = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.exportFileName);
    } catch (error) {
      console.log(error);
    }
  }
  generatePeriod(period: AssessmentPeriodDTO): void {
    const sheetName = AssessmentPeriodExport.buildAssessmentSheetName(extractQuarterFromName(period.name));
    this.ws = this.workbook.addWorksheet(sheetName);

    const userContributionsOfPeriod = this.buildDisplayPeriodsContributionLevels(
      period.id?.toString() || '',
      this.usersExport,
      false
    );
    this.addAssessmentInformationToWorkSheet(period, userContributionsOfPeriod);
    this.addEmptyRow();
    this.addEmptyRow();
    this.addUserInformationToWorksheet(userContributionsOfPeriod, false);
    this.setColumnWidth();
  }

  buildDisplayPeriodsContributionLevels(
    assessmentPeriodId: string,
    users: UserContributionLevelDisplayDTO[],
    isExportSummary: boolean
  ): UserContributionLevelDisplayDTO[] {
    const periodsContributionsSelected = this.periodsContributions.filter(pc =>
      assessmentPeriodId.includes(pc.assessmentPeriod?.id?.toString() || '-1')
    );
    const periodsContributionsByUserId = uniqBy(
      isExportSummary ? this.periodsContributions : periodsContributionsSelected,
      'user.id'
    ).filter(pc => {
      return users.find(user => user.id === +pc.user.code);
    });
    return uniqBy(periodsContributionsByUserId, 'user.id').map((pc, index) => {
      const userAccumulatedDrops = sumByField(
        isExportSummary
          ? this.periodsContributions.filter(p => p.user.id === pc.user.id)
          : periodsContributionsSelected.filter(p => p.user.id === pc.user.id),
        'periodAccumulatedDrops'
      );
      const userByUserId = users.find(user => user.id === +pc.user.code);
      const projectInformation = userByUserId?.project.filter(
        p => p.assessmentPeriodId.toString() === assessmentPeriodId
      );
      return {
        order: index + 1,
        id: pc.user.id,
        name: pc.user.fullName,
        project: isExportSummary
          ? (userByUserId?.project as ProjectInformationDTO[])
          : (projectInformation as ProjectInformationDTO[]),
        drops: userAccumulatedDrops,
        currentLevel: getContributionLevelTextFromDrops(userAccumulatedDrops)
      };
    });
  }

  addAssessmentInformationToWorkSheet(
    assessmentPeriod: AssessmentPeriodDTO,
    users: UserContributionLevelDisplayDTO[]
  ): void {
    const rows = this.buildAssessmentInformation(assessmentPeriod, users);
    this.addTitleRow(`Period Information`, rows[0].length, 1);
    this.addEmptyRow();

    rows.forEach((rowData, rowIndex) => {
      if (rowIndex === 0) {
        this.addRow(rowData, { border: STYLES.primaryTealBorderTop, fill: STYLES.lightTealFill }, (cell, index) => {
          if (index === 2) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      } else if (rowIndex === rows.length - 1) {
        this.addRow(rowData, { border: STYLES.primaryTealBorderBottom, fill: STYLES.lightTealFill }, (cell, index) => {
          if (index === 2) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      }
      this.interleaveRowStyle(rowData, rowIndex, (cell, index) => {
        if (index === 2) {
          cell.font = STYLES.boldFont;
        }
      });
    });
  }

  addRow(data: any[], style?: Style, callback?: callbackFn): Row {
    const row = this.ws.addRow(data);
    let maxHeight = 20;
    row.eachCell((cell, index) => {
      if (cell.value !== EMPTY) {
        if (typeof cell.value === 'number') {
          cell.alignment = STYLES.middleRightWrappedAlignment;
        } else {
          if (cell.value) {
            const values = cell.value.toString().split(/[\n\r]+/);

            for (const value of values) {
              let height = value.length;

              if (cell.font && cell.font.bold) {
                height *= 1.08;
              }
              maxHeight = Math.max(maxHeight, height);
            }
          }

          cell.alignment = STYLES.middleLeftWrappedAlignment;
        }
        if (cell.value === NA) {
          cell.font = STYLES.naFont;
          cell.alignment = STYLES.middleRightWrappedAlignment;
        }
        this.addStyleToCell(cell, style);
        if (callback) {
          callback(cell, index);
        }
      }
    });
    row.height = maxHeight;
    return row;
  }

  addUserInformationToWorksheet(users: UserContributionLevelDisplayDTO[], isExportSummary: boolean): void {
    const header = ['No.', 'ID', 'Fullname', 'Project', 'Current Level', 'Accumulated drops'];

    this.addTitleRow(`User list`, header.length, isExportSummary ? 1 : 10);

    this.addEmptyRow();
    this.addTableHeaderRow(header);
    const boldColumnIndex = 6;
    const usersRow = users
      .sort((preUser: UserContributionLevelDisplayDTO, currentUser: UserContributionLevelDisplayDTO) => {
        return ('' + preUser.name).localeCompare(currentUser.name);
      })
      .map((user, index) => this.buildUserInformation(user, index));

    usersRow.forEach((userRowData, userIndex) => {
      if (userIndex === usersRow.length - 1) {
        this.addRow(userRowData, { border: STYLES.primaryTealBorderBottom }, (cell, index) => {
          if (index === boldColumnIndex) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      }
      this.interleaveRowStyle(userRowData, userIndex, (cell, index) => {
        if (index === boldColumnIndex) {
          cell.font = STYLES.boldFont;
        }
      });
    });
  }

  addStyleToCell(cell: Cell, style: Style = {}): void {
    Object.keys(style).map(key => set(cell, key, get(style, key)));
  }

  addTableHeaderRow(header: string[]): void {
    const row = this.ws.addRow(header);
    row.eachCell(cell => {
      cell.font = STYLES.boldFont;
      cell.fill = STYLES.primaryTealFill;
    });
  }

  addTitleRow(title: string, mergeLength: number, rowPosition: number): void {
    const row = this.ws.addRow([title, ...Array(mergeLength - 2).fill('')]);
    this.mergeCells(row, 1, mergeLength);
    const address = `${row.getCell(1).address}`;
    this.ws.getCell(address).font = STYLES.h1Font;
    this.ws.getRow(rowPosition).height = 45;
  }

  addEmptyRow(): void {
    this.ws.addRow([]);
  }

  buildAssessmentInformation(assessmentPeriod: AssessmentPeriodDTO, users: UserContributionLevelDisplayDTO[]): any[] {
    const { name, startDate, endDate } = assessmentPeriod;
    const totalAssessmentPeriodDrops: number = users.reduce(function (totalAssessmentPeriodDrops, item) {
      return totalAssessmentPeriodDrops + item.drops;
    }, 0);
    const result = [
      ['Name', name],
      ['Start date', formatDateToDisplay(startDate)],
      ['End date', formatDateToDisplay(endDate)],
      ['Total users', users.length],
      ['Total Pollens', totalAssessmentPeriodDrops || NA]
    ];
    return result.map(assessmentInformation => [...Array(1).fill(EMPTY), ...assessmentInformation]);
  }

  buildUserInformation(user: UserContributionLevelDisplayDTO, index: number): any[] {
    return [
      index + 1,
      convertNumberToFourDigitsString(user.id),
      user.name,
      user.project.map(p => p.projectName).join(', '),
      user.currentLevel,
      user.drops
    ];
  }

  mergeCells(row: Row, from: number, to: number): void {
    this.ws.mergeCells(`${row.getCell(from).address}:${row.getCell(to).address}`);
  }

  interleaveRowStyle(data: any[], index: number, callbackFn?: callbackFn): Row {
    return index % 2 === 0
      ? this.addRow(data, { fill: STYLES.lightTealFill }, callbackFn)
      : this.addRow(data, {}, callbackFn);
  }

  setColumnWidth(): void {
    this.ws.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
  }

  get exportFileName(): string {
    const currentDate = moment(new Date()).format('YYYYMMDD');
    return `HIVE_Export By User_${currentDate}.xlsx`;
  }
}
